import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCashPayment,
  deleteCashPayment,
  deleteStatement,
  getCandidates,
  getGlobalCandidates,
  getStatement,
  listStatements,
  reassignStatementCompany,
  reconcileStatement,
  refreshMatching,
  updateCashPayment,
  uploadStatement,
  type CashPaymentInput,
  type ReconcileLine,
  type StatementListOptions,
} from '../lib/bankStatementsApi';
import type { UploadProgressHandler } from '../lib/fileUpload';
import { useActiveCompanyId } from '../store/useCompanyStore';

const KEY = ['bank-statements'] as const;

export function useBankStatements(page = 1, limit = 20, options: StatementListOptions = {}) {
  // Global firm switcher (multi-company): threaded internally so pages don't
  // have to pass it.
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const { sort = null, from = null, to = null, source = null, importBatchId = null } = options;
  return useQuery({
    queryKey: [
      ...KEY,
      'list',
      page,
      limit,
      activeCompanyId ?? null,
      sort,
      from,
      to,
      source,
      importBatchId,
    ],
    queryFn: () => listStatements(page, limit, activeCompanyId, options),
    placeholderData: (prev) => prev,
  });
}

export function useBankStatement(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getStatement(id as string),
    enabled: Boolean(id),
  });
}

export function reconCandidatesKey(id: string | undefined) {
  return [...KEY, 'candidates', id] as const;
}

export function useReconCandidates(id: string | undefined) {
  return useQuery({
    queryKey: reconCandidatesKey(id),
    queryFn: () => getCandidates(id as string),
    enabled: Boolean(id),
  });
}

export function useUploadStatement() {
  const qc = useQueryClient();
  return useMutation({
    // `companyId` pins the statement to a firm; omitted = auto-detect by CUI.
    mutationFn: (vars: { file: File; companyId?: string; onProgress?: UploadProgressHandler }) =>
      uploadStatement(vars.file, vars.companyId, vars.onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

export function useReassignStatementCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; companyId: string | null }) =>
      reassignStatementCompany(vars.id, vars.companyId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.id] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

export function useDeleteBankStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStatement(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
      // The delete also removes [bank:…] rows from the ledger and can revert
      // invoices, so every financial view must refetch.
      void qc.invalidateQueries({ queryKey: ['financials'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useReconcileStatement(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { lines: ReconcileLine[]; finalize: boolean }) =>
      reconcileStatement(id, vars.lines, vars.finalize),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

export function useRefreshMatching(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => refreshMatching(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
    },
  });
}

// --- Cash payments (cash register) ------------------------------------------

/** Statement-agnostic candidate master-data, for the cash payment modal. */
export function useGlobalCandidates(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'candidates', 'global'],
    queryFn: getGlobalCandidates,
    enabled,
  });
}

export function useCreateCashPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CashPaymentInput) => createCashPayment(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['financials'] });
    },
  });
}

export function useUpdateCashPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { txnId: string; input: CashPaymentInput }) =>
      updateCashPayment(vars.txnId, vars.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['financials'] });
    },
  });
}

export function useDeleteCashPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (txnId: string) => deleteCashPayment(txnId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['financials'] });
    },
  });
}
